import { ApiClientError, api, auth } from "@lumberset/api-client";
import {
  HelpLauncher,
  ReportProblemButton,
  ReportProblemDialog,
  useReportProblem,
} from "@lumberset/support-ui";
import { initials } from "@lumberset/takeoff-ui";
import {
  AppShell,
  Avatar,
  Badge,
  Button,
  DropdownMenu,
  EmptyState,
  LogoMark,
  Sidebar,
} from "@lumberset/ui";
import {
  AcademicCap,
  ArrowRightOnRectangle,
  BellAlert,
  BookOpen,
  Bug,
  Buildings,
  ChartActivity,
  ChartBar,
  CogSixTooth,
  CreditCard,
  CurrencyDollar,
  DocumentText,
  EllipsisHorizontal,
  Envelope,
  Gift,
  Lifebuoy,
  LockClosedSolid,
  Receipt,
  ReceiptPercent,
  ScrollText,
  UserGroup,
  Users,
} from "@medusajs/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { NOT_ADMIN_MESSAGE, NotAdminError } from "../lib/admin";
import { meQuery, supportStatsQuery } from "../lib/queries";
import { pendingPricesQuery } from "../lib/queries-prices";

/** Everything behind sign-in. Ensures a session and an Admin before any screen loads. */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    const { data: session } = await auth.getSession();
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });

    const me = await context.queryClient.ensureQueryData(meQuery).catch((error: unknown) => {
      // The endpoint is Staff only, so a 403 is a signed-in person who is not Staff at all.
      if (error instanceof ApiClientError && error.status === 403) return null;
      throw error;
    });

    // Signed in, but not an Admin. On one machine a Member's Yard app session reaches
    // this port too, so the session is left alone: the screen offers to sign out.
    if (me?.user.role !== "admin") throw new NotAdminError(session.user.email);
    return { user: me.user };
  },
  component: AppLayout,
  errorComponent: ({ error, reset }) =>
    error instanceof NotAdminError ? (
      <NotAdmin email={error.email} />
    ) : (
      <div className="flex min-h-dvh items-center justify-center">
        <EmptyState
          title="Something went wrong"
          description={error instanceof Error ? error.message : "Please try again."}
          action={
            <Button variant="secondary" onClick={reset}>
              Try again
            </Button>
          }
        />
      </div>
    ),
});

/** Signs out, forgets everything cached, and lands on the sign-in screen. */
function useSignOut() {
  const router = useRouter();
  const queryClient = useQueryClient();
  return async () => {
    await auth.signOut();
    queryClient.clear();
    await router.invalidate();
    router.history.push("/login");
  };
}

function NotAdmin({ email }: { email: string }) {
  const signOut = useSignOut();
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <EmptyState
        icon={<LockClosedSolid />}
        title={NOT_ADMIN_MESSAGE}
        description={`You are signed in as ${email}, which is not an Admin account. Sign out, then sign in as an Admin.`}
        action={
          <Button variant="secondary" onClick={signOut}>
            <ArrowRightOnRectangle /> Sign out
          </Button>
        }
      />
    </div>
  );
}

function AppLayout() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const report = useReportProblem();
  // The count on the Support entry: open Tickets that still need someone.
  const { data: stats } = useQuery({ ...supportStatsQuery, refetchInterval: 60_000 });
  const { data: prices } = useQuery(pendingPricesQuery);

  return (
    <>
      <AppShell
        sidebar={
          <>
            <Sidebar.Header>
              <div className="flex items-center gap-x-2.5 p-1">
                <LogoMark className="size-7" />
                <span className="min-w-0 flex-1">
                  <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                    LumberSet
                  </span>
                  <span className="txt-compact-xsmall block text-ui-fg-muted">Admin panel</span>
                </span>
              </div>
            </Sidebar.Header>

            <Sidebar.Body>
              <Sidebar.Item asChild>
                <Link to="/" activeOptions={{ exact: true }}>
                  <ChartBar /> Overview
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>People</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/yards">
                  <Buildings /> Workspaces
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/people">
                  <UserGroup /> People
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/staff">
                  <Users /> Staff
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/support">
                  <Lifebuoy />
                  <span className="flex-1">Support</span>
                  {stats && stats.open > 0 ? (
                    <Badge color="blue" rounded="full">
                      {stats.open}
                    </Badge>
                  ) : null}
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/announcements">
                  <BellAlert /> Announcements
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>Money</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/ledger">
                  <Receipt /> Ledger
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/billing-plans">
                  <CreditCard /> Billing plans
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/credit-packs">
                  <Gift /> Credit packs
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/coupons">
                  <ReceiptPercent /> Coupons
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/offers">
                  <DocumentText /> Custom plan offers
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>Content</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/help/articles">
                  <BookOpen /> Help articles
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/email-templates">
                  <Envelope /> Email templates
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/reference-prices">
                  <CurrencyDollar />
                  <span className="flex-1">Reference prices</span>
                  {prices && prices.pending > 0 ? (
                    <Badge color="orange" rounded="full">
                      {prices.pending}
                    </Badge>
                  ) : null}
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>Quality</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/accuracy">
                  <ChartActivity /> Accuracy
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/lessons">
                  <AcademicCap /> Reading lessons
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>Platform</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/settings">
                  <CogSixTooth /> Settings
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/audit-log">
                  <ScrollText /> Audit log
                </Link>
              </Sidebar.Item>
            </Sidebar.Body>

            <Sidebar.Footer>
              <ReportProblemButton onClick={report.start} isLoading={report.capturing} />
              <UserMenu name={user.name} email={user.email} onReport={report.start} />
            </Sidebar.Footer>
          </>
        }
        breadcrumbs={<div id="breadcrumbs" />}
      >
        <Outlet />
      </AppShell>

      <ReportProblemDialog
        client={api}
        {...report.dialog}
        source="staff"
        onViewTicket={(id) => navigate({ to: "/support/tickets/$id", params: { id } })}
        onViewTickets={() => navigate({ to: "/support/mine" })}
      />
      <HelpLauncher api={api} onOpenTicket={report.start} />
    </>
  );
}

function UserMenu({
  name,
  email,
  onReport,
}: {
  name: string;
  email: string;
  onReport: () => void;
}) {
  const signOut = useSignOut();

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger className="transition-fg flex w-full items-center gap-x-2.5 rounded-md p-1 text-left outline-none hover:bg-ui-bg-subtle-hover focus-visible:shadow-borders-focus data-[state=open]:bg-ui-bg-subtle-hover">
        <Avatar fallback={initials(name)} size="xsmall" shape="circle" />
        <span className="min-w-0 flex-1">
          <span className="txt-compact-small-plus block truncate text-ui-fg-subtle">{name}</span>
          <span className="txt-compact-xsmall block text-ui-fg-muted">Admin</span>
        </span>
        <EllipsisHorizontal className="shrink-0 text-ui-fg-muted" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="top" className="w-[236px]">
        <DropdownMenu.Label className="truncate">{email}</DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={onReport}>
          <Bug /> Report a problem
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={signOut}>
          <ArrowRightOnRectangle /> Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
